import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';

import { getGetUserBookingsQueryKey } from '@/common/api/endpoints/appointments/appointments';
import { getGetOralExamRunStateQueryKey } from '@/common/api/endpoints/oral-exams/oral-exams';
import { useAuthStore } from '@/common/auth/store';
import {
  ensureOralExamPractice,
  fetchOralExamPracticeAvailability,
  oralExamPracticeQueryKey,
  type OralExamPracticeEnsure,
} from '@/common/utils/oralExam/practiceApi';

/** Practice ensure resets server run state; refetch so an in-meeting terminal drops stale cache. */
export async function refetchOralExamRunStateAfterPracticeEnsure(
  queryClient: QueryClient,
  ensured: OralExamPracticeEnsure,
): Promise<void> {
  const bookingId = ensured.bookingId ?? ensured.sessionId;
  const queryKey = getGetOralExamRunStateQueryKey(
    ensured.courseId,
    ensured.moduleId,
    ensured.taskId,
    bookingId,
  );
  await queryClient.invalidateQueries({ queryKey });
  await queryClient.refetchQueries({ queryKey });
}

export function useOralExamPracticeAvailability(taskId: string | undefined, enabled = true) {
  const queryEnabled = enabled && Boolean(taskId);
  return useQuery({
    queryKey: taskId ? oralExamPracticeQueryKey(taskId) : ['oralExamPractice', 'disabled'],
    queryFn: () => fetchOralExamPracticeAvailability(taskId!),
    staleTime: 30_000,
    retry: false,
    enabled: queryEnabled,
  });
}

export function useEnsureOralExamPractice(taskId: string | undefined) {
  const queryClient = useQueryClient();
  const userId = useAuthStore((s) => s.user?.id);
  return useMutation({
    mutationFn: () => {
      if (!taskId) {
        throw new Error('Oral exam practice requires a task id');
      }
      return ensureOralExamPractice(taskId);
    },
    onSuccess: async (ensured) => {
      if (taskId) {
        await queryClient.invalidateQueries({ queryKey: oralExamPracticeQueryKey(taskId) });
      }
      await refetchOralExamRunStateAfterPracticeEnsure(queryClient, ensured);
      if (userId) {
        await queryClient.invalidateQueries({ queryKey: getGetUserBookingsQueryKey(userId) });
      }
    },
  });
}
